import { ReactComponentType } from 'strapi/component/types';
import { STRAPI_CONTENT_URL } from 'strapi/constants';
import { PageType, PageTypes } from 'strapi/page/types';
import { ReactComponentBuildResponse } from 'strapi/types';

import { collectImageComponentParams } from '../shared/collector';

import { defaultTestimonials } from './config';
import { Testimonial } from './Testimonial';
import { TestimonialProps, TestimonialStrapiComponent } from './types';

export const collectTestimonialComponentParams = (
  data: TestimonialStrapiComponent
): TestimonialProps | undefined => {
  // If we did not input testimonials on Strapi, we can default to `defaultTestimonials`
  if (!data?.cards?.length) {
    return defaultTestimonials;
  }

  const cards = data?.cards?.map((x) => ({
    title: x.title,
    name: x.name,
    subscribersText: x.subscribersText,
    image: collectImageComponentParams(x.image)!,
  }));

  // We need this check since the type on Strapi used to be different, and some legacy content might still be there
  const testimonialCards = cards.filter(
    (x) => x.title && x.subscribersText && x.image && x.name
  );

  // If the old content type is detected, we just return `defaultTestimonials`
  if (cards.length > 0 && cards.length !== testimonialCards.length) {
    return defaultTestimonials;
  }

  return {
    cards: testimonialCards.map((x) => ({
      ...x,
      image: {
        ...x.image,
        url:
          x.image.provider === 'local'
            ? `${STRAPI_CONTENT_URL}${x.image.url}`
            : x.image.url,
      },
    })),
  };
};

export const collectTestimonialParams = (
  _: PageType,
  page: PageTypes
): ReactComponentBuildResponse<typeof Testimonial> | undefined => {
  if (!('testimonial' in page) || !page.testimonial) {
    return undefined;
  }
  const props = collectTestimonialComponentParams(page.testimonial);

  if (!props) {
    return undefined;
  }

  return {
    component: ReactComponentType.Testimonial,
    props: props,
  };
};
